import React from "react";
import axios from "axios";
import { Lock, LogOut, Save, Plus, Trash2, RefreshCw, AlertCircle, CheckCircle2, X, Star } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "xs-admin-token";

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function Toast({ kind, msg, onClose }) {
  if (!msg) return null;
  const Icon = kind === "error" ? AlertCircle : CheckCircle2;
  const color = kind === "error" ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200";
  return (
    <div className={`fixed top-4 right-4 z-[100] rounded-xl border ${color} px-4 py-3 shadow-lg flex items-center gap-3 max-w-md`} data-testid="admin-toast">
      <Icon className="h-4 w-4 shrink-0" />
      <p className="text-sm font-medium flex-1">{msg}</p>
      <button onClick={onClose} aria-label="Close" className="opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
    </div>
  );
}

function LoginForm({ onSuccess, toast }) {
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      setToken(data.access_token);
      toast("success", "Logged in. Welcome back, Alex 👋");
      onSuccess();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Login failed";
      setErr(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-5" data-testid="admin-login">
      <form onSubmit={submit} className="xs-card p-8 max-w-md w-full">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
          <Lock className="h-5 w-5 text-[var(--xs-primary)]" />
        </div>
        <h1 className="font-display text-2xl font-black mt-4">Admin Login</h1>
        <p className="text-sm text-neutral-600 mt-1">Private dashboard for editing units & reviews.</p>
        <label className="block mt-6 text-xs font-semibold text-neutral-700">Password</label>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="mt-1 w-full rounded-xl border border-neutral-200 focus:border-[var(--xs-primary)] focus:ring-2 focus:ring-[var(--xs-primary-soft)] outline-none px-4 py-2.5 text-sm"
          placeholder="Enter admin password"
          required
        />
        {err && <p className="mt-3 text-xs text-red-600" data-testid="admin-login-error">{err}</p>}
        <button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--xs-primary)] hover:bg-[var(--xs-primary-hover)] disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-colors"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function UnitsEditor({ toast }) {
  const [units, setUnits] = React.useState([]);
  const [drafts, setDrafts] = React.useState({});
  const [savingId, setSavingId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/units`);
      setUnits(data || []);
    } catch (e) {
      toast("error", "Failed to load units");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  React.useEffect(() => { load(); }, [load]);

  const onField = (id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  };

  const save = async (unit) => {
    const draft = drafts[unit.id];
    if (!draft || Object.keys(draft).length === 0) return;
    setSavingId(unit.id);
    try {
      const payload = { ...draft };
      if (payload.rate_php !== undefined) payload.rate_php = parseInt(payload.rate_php, 10) || 0;
      const { data } = await axios.put(`${API}/admin/units/${unit.id}`, payload, { headers: authHeaders() });
      setUnits((arr) => arr.map((u) => u.id === unit.id ? { ...u, ...data } : u));
      setDrafts((d) => { const { [unit.id]: _, ...rest } = d; return rest; });
      toast("success", `${data.name} updated.`);
    } catch (e) {
      toast("error", e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <p className="text-sm text-neutral-500">Loading 30 units…</p>;

  return (
    <div className="space-y-4" data-testid="admin-units-editor">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-600">{units.length} units · click any field to edit, then Save</p>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-[var(--xs-primary)]">
          <RefreshCw className="h-3.5 w-3.5" /> Reload
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {units.map((u) => {
          const draft = drafts[u.id] || {};
          const hasDraft = Object.keys(draft).length > 0;
          return (
            <div key={u.id} className="xs-card p-4" data-testid={`admin-unit-${u.id}`}>
              <div className="aspect-[4/3] overflow-hidden rounded-xl bg-neutral-100">
                <img src={draft.image || u.image} alt={u.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-widest text-[var(--xs-primary)]">{u.location_name} · {u.type}</p>
              <label className="block mt-2 text-[11px] font-semibold text-neutral-500">Name</label>
              <input value={draft.name ?? u.name} onChange={(e) => onField(u.id, "name", e.target.value)} className="w-full text-sm font-bold border-b border-transparent hover:border-neutral-200 focus:border-[var(--xs-primary)] outline-none py-1" />
              <label className="block mt-2 text-[11px] font-semibold text-neutral-500">Rate (₱/night)</label>
              <input type="number" value={draft.rate_php ?? u.rate_php} onChange={(e) => onField(u.id, "rate_php", e.target.value)} className="w-full text-sm border-b border-transparent hover:border-neutral-200 focus:border-[var(--xs-primary)] outline-none py-1" />
              <label className="block mt-2 text-[11px] font-semibold text-neutral-500">Image URL</label>
              <input value={draft.image ?? u.image ?? ""} onChange={(e) => onField(u.id, "image", e.target.value)} className="w-full text-xs border-b border-transparent hover:border-neutral-200 focus:border-[var(--xs-primary)] outline-none py-1 truncate" />
              <label className="block mt-2 text-[11px] font-semibold text-neutral-500">Description</label>
              <textarea rows={3} value={draft.description ?? u.description ?? ""} onChange={(e) => onField(u.id, "description", e.target.value)} className="w-full text-xs border border-neutral-200 hover:border-neutral-300 focus:border-[var(--xs-primary)] rounded-lg p-2 mt-1 outline-none resize-none" />
              <button
                onClick={() => save(u)}
                disabled={!hasDraft || savingId === u.id}
                data-testid={`admin-unit-save-${u.id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-semibold px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--xs-primary)] hover:bg-[var(--xs-primary-hover)] text-white"
              >
                <Save className="h-3.5 w-3.5" /> {savingId === u.id ? "Saving…" : hasDraft ? "Save changes" : "No changes"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewsEditor({ toast }) {
  const [reviews, setReviews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ name: "", location: "Philippines", rating: 5, stay: "Verified Booking", date: "", comment: "" });
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data || []);
    } catch {
      toast("error", "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  React.useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reviews`, form, { headers: authHeaders() });
      setReviews((arr) => [data, ...arr]);
      setForm({ name: "", location: "Philippines", rating: 5, stay: "Verified Booking", date: "", comment: "" });
      toast("success", `Review by ${data.name} added.`);
    } catch (e) {
      toast("error", e?.response?.data?.detail || "Could not save review");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete the review by ${name}?`)) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders() });
      setReviews((arr) => arr.filter((r) => r.id !== id));
      toast("success", "Review deleted.");
    } catch (e) {
      toast("error", e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-reviews-editor">
      <form onSubmit={create} className="xs-card p-5 mb-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Plus className="h-4 w-4 text-[var(--xs-primary)]" /> Add a new review</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-neutral-500">Guest name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-review-name" className="w-full mt-1 text-sm border border-neutral-200 focus:border-[var(--xs-primary)] outline-none rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-neutral-500">Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full mt-1 text-sm border border-neutral-200 focus:border-[var(--xs-primary)] outline-none rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-neutral-500">Rating</label>
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value, 10) })} className="w-full mt-1 text-sm border border-neutral-200 focus:border-[var(--xs-primary)] outline-none rounded-lg px-3 py-2 bg-white">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-neutral-500">Stay type</label>
            <input value={form.stay} onChange={(e) => setForm({ ...form, stay: e.target.value })} className="w-full mt-1 text-sm border border-neutral-200 focus:border-[var(--xs-primary)] outline-none rounded-lg px-3 py-2" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold text-neutral-500">Date label (e.g. "Today", "Jan 2026")</label>
            <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="defaults to 'Just now'" className="w-full mt-1 text-sm border border-neutral-200 focus:border-[var(--xs-primary)] outline-none rounded-lg px-3 py-2" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold text-neutral-500">Comment *</label>
            <textarea required rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="admin-review-comment" className="w-full mt-1 text-sm border border-neutral-200 focus:border-[var(--xs-primary)] outline-none rounded-lg px-3 py-2 resize-none" />
          </div>
        </div>
        <button type="submit" disabled={busy || !form.name || !form.comment} data-testid="admin-review-submit"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[var(--xs-primary)] hover:bg-[var(--xs-primary-hover)] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold">
          <Plus className="h-4 w-4" /> {busy ? "Adding…" : "Add review"}
        </button>
      </form>

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-neutral-600">{reviews.length} reviews · newest first</p>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-[var(--xs-primary)]">
          <RefreshCw className="h-3.5 w-3.5" /> Reload
        </button>
      </div>

      {loading ? <p className="text-sm text-neutral-500">Loading…</p> : (
        <ul className="space-y-2">
          {reviews.slice(0, 50).map((r) => (
            <li key={r.id} className="xs-card p-4 flex items-start gap-3" data-testid={`admin-review-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-neutral-500">· {r.location} · {r.date}</span>
                  <span className="inline-flex items-center gap-0.5 text-[var(--xs-primary)]">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
                  </span>
                </div>
                <p className="text-sm text-neutral-800 mt-1 line-clamp-2">{r.comment}</p>
              </div>
              <button onClick={() => remove(r.id, r.name)} data-testid={`admin-review-delete-${r.id}`} aria-label="Delete review"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-red-300 hover:text-red-600 text-neutral-500 shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {reviews.length > 50 && <p className="text-xs text-neutral-500 mt-3 text-center">Showing 50 of {reviews.length} — use the public page for the full list.</p>}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = React.useState(() => Boolean(getToken()));
  const [tab, setTab] = React.useState("units");
  const [toastState, setToastState] = React.useState({ kind: "", msg: "" });
  const toast = (kind, msg) => {
    setToastState({ kind, msg });
    setTimeout(() => setToastState({ kind: "", msg: "" }), 3500);
  };

  // Verify token is still valid on mount
  React.useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/admin/me`, { headers: authHeaders() }).catch(() => {
      setToken(null);
      setAuthed(false);
    });
  }, [authed]);

  const logout = () => {
    setToken(null);
    setAuthed(false);
    toast("success", "Logged out.");
  };

  return (
    <div data-testid="admin-page" className="min-h-[80vh]">
      <Seo title="Admin Dashboard" description="Private admin panel for Xanders Staycations." path="/admin" />
      <Toast {...toastState} onClose={() => setToastState({ kind: "", msg: "" })} />

      {!authed ? (
        <LoginForm onSuccess={() => setAuthed(true)} toast={toast} />
      ) : (
        <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-10 pb-20">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <p className="xs-eyebrow">Admin Dashboard</p>
              <h1 className="font-display text-3xl font-black mt-1">Manage content</h1>
              <p className="text-sm text-neutral-600 mt-1">Edit any unit or add/delete reviews. Changes are live instantly.</p>
            </div>
            <button onClick={logout} data-testid="admin-logout"
                    className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] px-4 py-2 text-sm font-semibold">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>

          <div className="flex gap-2 mb-6 border-b border-neutral-200">
            {[{ k: "units", l: "Units (30)" }, { k: "reviews", l: "Reviews" }].map(({ k, l }) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                      className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                        tab === k ? "border-[var(--xs-primary)] text-[var(--xs-primary)]" : "border-transparent text-neutral-600 hover:text-neutral-900"
                      }`}>
                {l}
              </button>
            ))}
          </div>

          {tab === "units" ? <UnitsEditor toast={toast} /> : <ReviewsEditor toast={toast} />}
        </div>
      )}
    </div>
  );
}
